import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { Plus, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Customers() {
  const { tenant } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/customers").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]" data-testid="customers-page">
      <PageHeader
        title="Customers"
        subtitle="Tenant-scoped customer master."
        action={
          <button onClick={() => setOpen(true)} data-testid="add-customer-btn"
            className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm font-medium rounded-sm transition-colors">
            <Plus size={14} /> New customer
          </button>
        }
      />

      <div className="mt-8 border border-[#0A0A0A] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Phone</Th>
              <Th align="right">Credit limit</Th>
              <Th align="right">Outstanding</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c, i) => (
              <tr key={c.id} data-testid={`customer-row-${i}`} className="border-b border-[#DEE2E6] hover:bg-[#F8F9FA]">
                <Td><div className="font-medium">{c.name}</div></Td>
                <Td className="text-[#495057]">{c.email || "—"}</Td>
                <Td className="text-[#495057]">{c.phone || "—"}</Td>
                <Td align="right" className="tabular-nums">{(c.credit_limit || 0).toLocaleString()}</Td>
                <Td align="right" className={`tabular-nums font-semibold ${c.balance > 0 ? "text-[#D90429]" : "text-[#0B7A75]"}`}>
                  {tenant?.currency || ""} {(c.balance || 0).toLocaleString()}
                </Td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-[#495057]">No customers yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && <CustomerForm onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function CustomerForm({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", credit_limit: 0 });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      await api.post("/customers", { ...form, credit_limit: parseFloat(form.credit_limit || 0) });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="New customer" onClose={onClose} testid="customer-modal">
      <form onSubmit={save} className="space-y-4" data-testid="customer-form">
        <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="customer-name" />
        <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="customer-email" />
        <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="customer-phone" />
        <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="customer-address" />
        <Field label="Credit limit" type="number" value={form.credit_limit} onChange={(v) => setForm({ ...form, credit_limit: v })} testid="customer-credit" />
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" disabled={saving} data-testid="customer-save"
            className="px-4 py-2 text-sm bg-[#0A0A0A] hover:bg-[#FF4F00] text-white">Save</button>
        </div>
      </form>
    </Modal>
  );
}

export function Th({ children, align = "left" }) {
  return <th className={`px-2 sm:px-4 py-2.5 sm:py-3 text-${align} font-semibold whitespace-nowrap`}>{children}</th>;
}
export function Td({ children, align = "left", className = "" }) {
  return <td className={`px-2 sm:px-4 py-2.5 sm:py-3 text-${align} ${className}`}>{children}</td>;
}

export function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">{label}</span>
      <input
        type={type}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm focus:border-[#0A0A0A] focus:outline-none"
      />
    </label>
  );
}

export function Modal({ title, onClose, children, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-2 sm:p-4" onClick={onClose} data-testid={testid}>
      <div onClick={(e) => e.stopPropagation()} className={`bg-white border-2 border-[#0A0A0A] ${wide ? "max-w-3xl" : "max-w-md"} w-full max-h-[92vh] sm:max-h-[85vh] overflow-y-auto`}>
        <div className="px-4 sm:px-5 py-3 border-b border-[#0A0A0A] flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="font-heading font-semibold tracking-tight truncate pr-2">{title}</div>
          <button onClick={onClose} className="p-1 hover:bg-[#F8F9FA] flex-shrink-0" data-testid="modal-close"><X size={16} /></button>
        </div>
        <div className="p-4 sm:p-5">{children}</div>
      </div>
    </div>
  );
}
